/**
 * Presentations service — Firestore CRUD for the `presentations` collection:
 * admin-curated Canva (or any web) slide decks that mentors teach from and
 * students revisit, shown alongside the PDF library in the Resources area.
 *
 * A presentation document:
 *   {
 *     subject,      // course subject string from admin/constants SUBJECTS
 *                   // (e.g. "Python Level-2") — groups the deck in the library.
 *     title,        // display title, e.g. "Unit 1 — Variables & Types"
 *     description,  // one-line blurb shown on the card
 *     url,          // the Canva share link (…/view) opened / embedded
 *     active,       // bool — hide a deck without deleting it
 *     createdAt, updatedAt
 *   }
 *
 * Unlike PDFs (read straight from Storage) a Canva deck is just a link, so it
 * needs a lightweight Firestore record. Reads are open to any signed-in user;
 * only admins author decks (see firestore.rules).
 */
import firestoreService from './firestore.service';

const PRESENTATIONS = 'presentations';

/** Whitelist the fields we persist so stray form state never leaks in. */
function sanitize(input = {}) {
  return {
    subject: (input.subject || '').trim(),
    title: (input.title || '').trim(),
    description: (input.description || '').trim(),
    url: (input.url || '').trim(),
    active: input.active !== false, // default active
  };
}

export const presentationsService = {
  /** Realtime subscription to every presentation (admin management view). */
  subscribe(onData, onError) {
    return firestoreService.subscribeCollection(
      PRESENTATIONS,
      [],
      (rows) => onData([...rows].sort(byOrder)),
      onError
    );
  },

  /**
   * Realtime subscription to every active (visible) deck. Active-filtered and
   * sorted client-side — the deck catalogue is small and staff-curated, so no
   * server index is needed. Powers the Presentations section in the
   * student/mentor Resources library, grouped by subject in the UI.
   */
  subscribeActive(onData, onError) {
    return firestoreService.subscribeCollection(
      PRESENTATIONS,
      [],
      (rows) => onData(rows.filter((r) => r.active !== false).sort(byOrder)),
      onError
    );
  },

  create(data) {
    return firestoreService.addDocument(PRESENTATIONS, sanitize(data));
  },

  update(id, data) {
    return firestoreService.updateDocument(PRESENTATIONS, id, sanitize(data));
  },

  remove(id) {
    return firestoreService.deleteDocument(PRESENTATIONS, id);
  },
};

/** Newest first, then title — a stable order without a composite index. */
function byOrder(a, b) {
  const ta = a.createdAt?.toMillis?.() ?? 0;
  const tb = b.createdAt?.toMillis?.() ?? 0;
  if (ta !== tb) return tb - ta;
  return (a.title || '').localeCompare(b.title || '');
}

export default presentationsService;
